// =============================================================================
// parametresVue.js : un lien vers la vue DÉJÀ ouverte (30/09/2026).
// -----------------------------------------------------------------------------
// Un lien de notification (centre, ou notification système relayée par le
// service worker), la palette ou un bouton qui vise la vue COURANTE ne la
// remonte jamais : un encart en ligne rempli (« Nouveau sondage », création
// d'une consultation, proposition de modification d'un projet) disparaîtrait
// avec sa saisie, sans dialogue. changerVue (App.jsx) annonce les paramètres
// par l'événement aramis:parametres-vue ; chaque vue visée les reçoit EN
// DIRECT (useParametresVue) et ouvre l'objet sans démonter ce qui est en
// cours. Une vue qui ne sait pas le faire sans perte le DIT (avis AvisLien,
// « Y aller » est le geste qui applique), jamais un silence (verrous :
// src/lienVueCourante.test.js, src/notificationsLiens.test.js,
// src/retourArriereMemeVue.test.js).
// Seul un GESTE annonce des paramètres (navTo), jamais l'historique : le retour
// arrière ou avant sur la même vue n'annonce rien et la vue reste telle quelle,
// comme avant le lot ; l'adresse d'une entrée d'historique n'est jamais
// appliquée sous une fenêtre de travail ouverte (relecture du 30/09). Une
// fenêtre de travail (ModalShell) ouverte passe d'abord par son dialogue de
// choix : navTo s'arrête sur differerSiFenetreOuverte (uxComponents.js), le
// retour arrière sur gardeHistorique (gardeHistorique.js, 04/10/2026).
// =============================================================================
import { useEffect, useRef } from "react";

export const EVENEMENT_PARAMETRES_VUE = "aramis:parametres-vue";

/**
 * Annonce à la vue courante ses nouveaux paramètres. Livrés par l'événement,
 * ils ne sont pas relus par un montage ultérieur (l'adresse les garde). Sans
 * paramètres (vue choisie sans objet), rien n'est annoncé.
 * @param {string} vue
 * @param {object|null} params
 */
export function annoncerParametresVue(vue, params) {
  if (!params || typeof params !== "object") return;
  try { window.dispatchEvent(new CustomEvent(EVENEMENT_PARAMETRES_VUE, { detail: { vue, params } })); } catch { /* très vieux navigateur */ }
  try { sessionStorage.removeItem("aramis_nav_params"); } catch { /* stockage indisponible */ }
}

/**
 * La vue montée reçoit ses nouveaux paramètres : `appliquer(params)` n'est
 * appelé qu'avec l'objet ANNONCÉ par un geste, jamais avec l'adresse (qui, après
 * un retour arrière, est celle d'une autre entrée d'historique). Toujours la
 * DERNIÈRE version d'`appliquer` (état courant).
 * @param {function(object): void} appliquer
 */
export function useParametresVue(appliquer) {
  const ref = useRef(appliquer);
  ref.current = appliquer;
  useEffect(() => {
    const ecoute = (e) => {
      const p = e && e.detail && e.detail.params;
      if (p && typeof p === "object") ref.current(p);
    };
    window.addEventListener(EVENEMENT_PARAMETRES_VUE, ecoute);
    return () => window.removeEventListener(EVENEMENT_PARAMETRES_VUE, ecoute);
  }, []);
}

/**
 * Récrit l'entrée d'historique COURANTE (replaceState, jamais une entrée de
 * plus) pour que l'adresse dise ce qui est affiché : une clé à null est
 * retirée, les autres sont posées ; la vue et les autres paramètres restent,
 * et `history.state.params` suit. L'état est recopié : la position de l'entrée
 * (gardeHistorique.js) reste. Sans changement, rien n'est écrit.
 * @param {Object<string, (string|number|null)>} modifs
 */
export function reecrireAdresse(modifs) {
  try {
    const qp = new URLSearchParams(window.location.search);
    const etat = window.history.state && typeof window.history.state === "object" ? { ...window.history.state } : null;
    const params = etat && etat.params && typeof etat.params === "object" ? { ...etat.params } : null;
    let change = false;
    for (const [k, v] of Object.entries(modifs || {})) {
      if (v == null) {
        if (qp.has(k)) { qp.delete(k); change = true; }
        if (params) delete params[k];
      } else {
        if (qp.get(k) !== String(v)) { qp.set(k, String(v)); change = true; }
        if (params) params[k] = v;
      }
    }
    if (!change) return;
    if (params) etat.params = params;
    const qs = qp.toString();
    window.history.replaceState(etat, "", window.location.pathname + (qs ? "?" + qs : "") + window.location.hash);
  } catch { /* historique indisponible */ }
}

/**
 * Le lien d'ARRIVÉE sur la vue (adresse, ou paramètres d'un navTo depuis une
 * autre vue) : `lire()` n'est appelé qu'UNE fois, au montage. `appliquer(p)`
 * est rejoué à chaque changement de `deps` tant qu'il rend false (l'objet visé
 * n'est pas encore chargé), puis plus jamais, et les clés `cles` sont retirées
 * de l'adresse. Relue à chaque arrivée de données, l'adresse rouvrait l'objet
 * du lien par-dessus ce que la personne avait ouvert depuis, et sa saisie
 * était perdue sans dialogue (Questions, relecture du 30/09 ; même garde que
 * lienMontageTraite des Réunions). Ensuite, seuls les paramètres ANNONCÉS
 * comptent (useParametresVue).
 * @param {{ lire: function(): (object|null), appliquer: function(object): (boolean|void), cles?: string[] }} lien
 * @param {Array} deps
 */
export function useLienAuMontage({ lire, appliquer, cles = [] }, deps) {
  const lien = useRef(undefined); // undefined : adresse pas encore lue ; null : rien, ou appliqué
  const ref = useRef(appliquer);
  ref.current = appliquer;
  useEffect(() => {
    if (lien.current === undefined) {
      const p = lire();
      lien.current = p && typeof p === "object" ? p : null;
    }
    if (!lien.current || ref.current(lien.current) === false) return;
    lien.current = null;
    if (cles.length) reecrireAdresse(Object.fromEntries(cles.map((k) => [k, null])));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
